import { useState, useEffect, useCallback } from "react";
import API, { formatWIB, formatWIBDate } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  UserCircle,
  Plus,
  PencilSimple,
  Key,
  Power,
  Shield,
  User as UserIcon,
} from "@phosphor-icons/react";

function UserRow({ u, onEdit, onResetPassword, onToggleActive, currentUserId }) {
  const isCurrentUser = u.id === currentUserId;
  return (
    <tr data-testid={`user-row-${u.email}`} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-sm flex items-center justify-center text-xs font-semibold text-white ${u.role === "admin" ? "bg-violet-600" : "bg-zinc-600"}`}>
            {u.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div>
            <p className="text-sm font-medium text-zinc-900">{u.name} {isCurrentUser && <span className="text-[10px] text-zinc-400">(Anda)</span>}</p>
            <p className="text-[11px] text-zinc-500 font-mono">{u.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`text-[10px] uppercase font-semibold tracking-wider px-2 py-1 rounded-sm ${u.role === "admin" ? "bg-violet-50 text-violet-700" : "bg-zinc-100 text-zinc-600"}`}>
          {u.role === "admin" ? <><Shield size={10} className="inline mr-1" />Admin</> : <><UserIcon size={10} className="inline mr-1" />Operator</>}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className={`text-[10px] font-semibold px-2 py-1 rounded-sm ${u.is_active !== false ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
          {u.is_active !== false ? "Aktif" : "Nonaktif"}
        </span>
      </td>
      <td className="px-4 py-3 text-xs font-mono text-zinc-500">
        {u.last_login ? formatWIB(u.last_login) : "-"}
      </td>
      <td className="px-4 py-3 text-xs font-mono text-zinc-500">
        {u.created_at ? formatWIBDate(u.created_at) : "-"}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <Button data-testid={`edit-user-${u.email}`} variant="ghost" size="sm" onClick={() => onEdit(u)} className="h-7 w-7 p-0">
            <PencilSimple size={14} />
          </Button>
          <Button data-testid={`reset-pw-${u.email}`} variant="ghost" size="sm" onClick={() => onResetPassword(u)} className="h-7 w-7 p-0">
            <Key size={14} />
          </Button>
          {!isCurrentUser && (
            <Button data-testid={`toggle-active-${u.email}`} variant="ghost" size="sm" onClick={() => onToggleActive(u)} className="h-7 w-7 p-0">
              <Power size={14} className={u.is_active !== false ? "text-red-500" : "text-emerald-500"} />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function UserManagementPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "operator" });
  const [editForm, setEditForm] = useState({ name: "", role: "" });
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const [changePw, setChangePw] = useState(false);
  const [changePwForm, setChangePwForm] = useState({ current: "", newPw: "" });

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await API.get("/users");
      setUsers(data.users || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCreate = async () => {
    if (!form.email || !form.password || !form.name) return;
    setSaving(true);
    try {
      await API.post("/users", form);
      setShowCreate(false);
      setForm({ email: "", password: "", name: "", role: "operator" });
      loadUsers();
    } catch (e) { alert(e.response?.data?.detail || "Gagal membuat user"); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      await API.put(`/users/${editUser.id}`, editForm);
      setEditUser(null);
      loadUsers();
    } catch (e) { alert(e.response?.data?.detail || "Gagal update"); }
    finally { setSaving(false); }
  };

  const handleResetPw = async () => {
    if (!resetPwUser || !newPassword) return;
    setSaving(true);
    try {
      await API.post(`/users/${resetPwUser.id}/reset-password`, { new_password: newPassword });
      setResetPwUser(null);
      setNewPassword("");
      alert("Password berhasil direset");
    } catch (e) { alert(e.response?.data?.detail || "Gagal reset"); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (u) => {
    const newStatus = u.is_active === false ? true : false;
    const action = newStatus ? "mengaktifkan" : "menonaktifkan";
    if (!window.confirm(`${action} akun ${u.name} (${u.email})?`)) return;
    try {
      await API.put(`/users/${u.id}`, { is_active: newStatus });
      loadUsers();
    } catch (e) { alert(e.response?.data?.detail || "Gagal"); }
  };

  const handleChangePw = async () => {
    if (!changePwForm.current || !changePwForm.newPw) return;
    setSaving(true);
    try {
      await API.post("/users/change-password", { current_password: changePwForm.current, new_password: changePwForm.newPw });
      setChangePw(false);
      setChangePwForm({ current: "", newPw: "" });
      alert("Password berhasil diubah");
    } catch (e) { alert(e.response?.data?.detail || "Gagal"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-sm text-zinc-400 font-mono">Loading...</p></div>;

  return (
    <div data-testid="user-management-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">User Management</h1>
          <p className="text-sm text-zinc-500 mt-1">{users.length} user terdaftar</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="change-own-pw-btn" variant="outline" onClick={() => setChangePw(true)} className="text-xs h-8 gap-1.5">
            <Key size={14} /> Ganti Password Saya
          </Button>
          <Button data-testid="create-user-btn" onClick={() => setShowCreate(true)} className="text-xs h-8 gap-1.5 bg-zinc-900 text-white hover:bg-zinc-800">
            <Plus size={14} /> Tambah User
          </Button>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreate && (
        <div className="border border-zinc-300 bg-white rounded-sm p-4 space-y-3">
          <h3 className="text-sm font-semibold text-zinc-800">Tambah User Baru</h3>
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="create-name" placeholder="Nama" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-8 text-sm" />
            <Input data-testid="create-email" placeholder="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="h-8 text-sm" />
            <Input data-testid="create-password" placeholder="Password" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="h-8 text-sm" />
            <select data-testid="create-role" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="h-8 text-sm border border-zinc-300 rounded-sm px-2">
              <option value="operator">Operator</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowCreate(false)} className="text-xs h-7">Batal</Button>
            <Button data-testid="create-submit" onClick={handleCreate} disabled={saving} className="text-xs h-7 bg-zinc-900 text-white hover:bg-zinc-800">
              {saving ? "Menyimpan..." : "Simpan"}
            </Button>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <div className="border border-zinc-300 bg-white rounded-sm p-4 space-y-3">
          <h3 className="text-sm font-semibold text-zinc-800">Edit: {editUser.name} ({editUser.email})</h3>
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="edit-name" placeholder="Nama" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="h-8 text-sm" />
            <select data-testid="edit-role" value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })} className="h-8 text-sm border border-zinc-300 rounded-sm px-2">
              <option value="operator">Operator</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setEditUser(null)} className="text-xs h-7">Batal</Button>
            <Button data-testid="edit-submit" onClick={handleEdit} disabled={saving} className="text-xs h-7 bg-zinc-900 text-white hover:bg-zinc-800">Simpan</Button>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPwUser && (
        <div className="border border-zinc-300 bg-white rounded-sm p-4 space-y-3">
          <h3 className="text-sm font-semibold text-zinc-800">Reset Password: {resetPwUser.name}</h3>
          <Input data-testid="reset-pw-input" type="password" placeholder="Password baru" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="h-8 text-sm max-w-xs" />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { setResetPwUser(null); setNewPassword(""); }} className="text-xs h-7">Batal</Button>
            <Button data-testid="reset-pw-submit" onClick={handleResetPw} disabled={saving} className="text-xs h-7 bg-zinc-900 text-white hover:bg-zinc-800">Reset</Button>
          </div>
        </div>
      )}

      {/* Change Own Password */}
      {changePw && (
        <div className="border border-zinc-300 bg-white rounded-sm p-4 space-y-3">
          <h3 className="text-sm font-semibold text-zinc-800">Ganti Password Anda</h3>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <Input data-testid="change-pw-current" type="password" placeholder="Password lama" value={changePwForm.current} onChange={e => setChangePwForm({ ...changePwForm, current: e.target.value })} className="h-8 text-sm" />
            <Input data-testid="change-pw-new" type="password" placeholder="Password baru" value={changePwForm.newPw} onChange={e => setChangePwForm({ ...changePwForm, newPw: e.target.value })} className="h-8 text-sm" />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setChangePw(false)} className="text-xs h-7">Batal</Button>
            <Button data-testid="change-pw-submit" onClick={handleChangePw} disabled={saving} className="text-xs h-7 bg-zinc-900 text-white hover:bg-zinc-800">Ubah</Button>
          </div>
        </div>
      )}

      {/* User Table */}
      <div className="border border-zinc-200 rounded-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">User</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Role</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Status</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Login Terakhir</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Dibuat</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <UserRow key={u.id} u={u} currentUserId={currentUser?.id}
                  onEdit={(u) => { setEditUser(u); setEditForm({ name: u.name, role: u.role }); }}
                  onResetPassword={(u) => setResetPwUser(u)}
                  onToggleActive={handleToggleActive}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
